import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import { FiRefreshCcw, FiChevronDown, FiX, FiSearch } from "react-icons/fi";
import "./NewCustomAsyncSelectDropdown.css";

// use the first element.
const normalizeValue = (value, isMulti) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;
  // Single object passed for single-select
  if (typeof value === "object" && value.value !== undefined) return [value];
  return [];
};

const UniqueAsyncSelect = ({
  // Core props
  options = [],
  value,                          // null | object | object[]
  onChange,
  placeholder = "Select options...",

  // Event handlers
  onRefresh,
  onInputChange,
  onMenuScrollToBottom,
  onSearchScrollToBottom,
  onMenuOpen,

  // State props
  isLoading = false,
  isSearching: externalIsSearching = false,
  isSearchable = true,
  isMulti = true,
  isDisabled = false,
  isClearable = false,

  // Text props
  loadingMessage = "Loading...",
  noOptionsMessage = "No options found",
  noSearchResultsMessage = "No results found",

  // Customization props
  formatOptionLabel,
  loadOptions,
  defaultOptions = [],
  maxSelectedHeight = 150,
  maxMenuHeight = 300,
}) => {
  // UI State
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayedOptions, setDisplayedOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState(() =>
    normalizeValue(value, isMulti),
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedContainerHeight, setSelectedContainerHeight] = useState("auto");
  const [shouldScroll, setShouldScroll] = useState(false);

  // Loading states
  const [isLocalSearching, setIsLocalSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");

  // Refs
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const selectedContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const rafTimeoutRef = useRef(null);

  // Determine mode
  const isAsyncMode = !!loadOptions;
  const isSearching = externalIsSearching || isLocalSearching;

  // ── Sync internal selectedValues whenever the external value prop changes ──
  // This is the KEY FIX: normalizeValue handles both single objects and arrays,
  // so single-select (category / classroom) always gets picked up correctly.
  useEffect(() => {
    setSelectedValues(normalizeValue(value, isMulti));
  }, [value, isMulti]);

  // Initialize options for ASYNC mode only -- the sync/static case (no
  // loadOptions) is fully handled by the filtering effect below, including
  // showing the full list when searchTerm is empty. This used to also run
  // for the sync case, unconditionally resetting displayedOptions to the
  // full (unfiltered) `options` prop -- and because `defaultOptions`
  // defaults to a fresh `[]` on every render that doesn't pass it, this
  // effect re-fired on *every* render (e.g. just hovering an option), wiping
  // out whatever the filter effect had just computed and making search look
  // like it silently did nothing.
  useEffect(() => {
    if (isAsyncMode && defaultOptions.length > 0 && !isSearching && searchTerm === "") {
      setDisplayedOptions(defaultOptions);
    }
  }, [defaultOptions, isAsyncMode, isSearching, searchTerm]);

  // Update selected container height dynamically with RAF for smooth updates
  const updateContainerHeight = useCallback(() => {
    if (selectedContainerRef.current) {
      if (rafTimeoutRef.current) {
        cancelAnimationFrame(rafTimeoutRef.current);
      }

      rafTimeoutRef.current = requestAnimationFrame(() => {
        if (selectedContainerRef.current) {
          const scrollHeight = selectedContainerRef.current.scrollHeight;

          if (selectedValues.length === 0) {
            setSelectedContainerHeight("40px");
            setShouldScroll(false);
          } else if (scrollHeight <= maxSelectedHeight) {
            setSelectedContainerHeight(`${scrollHeight}px`);
            setShouldScroll(false);
          } else {
            setSelectedContainerHeight(`${maxSelectedHeight}px`);
            setShouldScroll(true);
          }
        }
      });
    }
  }, [maxSelectedHeight, selectedValues.length]);

  useEffect(() => {
    updateContainerHeight();
    return () => {
      if (rafTimeoutRef.current) {
        cancelAnimationFrame(rafTimeoutRef.current);
      }
    };
  }, [selectedValues, updateContainerHeight]);

  useEffect(() => {
    updateContainerHeight();
    const handleResize = () => updateContainerHeight();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateContainerHeight]);

  // Handle local filtering for sync mode
  useEffect(() => {
    if (!isAsyncMode) {
      if (searchTerm.trim() === "") {
        setDisplayedOptions(options);
      } else {
        const filtered = options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setDisplayedOptions(filtered);
      }
      setHighlightedIndex(-1);
    }
  }, [searchTerm, options, isAsyncMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search input with debounce for async loading
  const handleSearchChange = async (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setCurrentSearchTerm(val);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (isAsyncMode && loadOptions) {
      setIsLocalSearching(true);
      setIsLoadingMore(false);

      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await loadOptions(val, 1);
          setDisplayedOptions(results || []);
          if (onInputChange && typeof onInputChange === "function") {
            onInputChange(val);
          }
        } catch (error) {
          console.error("Error loading options:", error);
          setDisplayedOptions([]);
        } finally {
          setIsLocalSearching(false);
        }
      }, 500);
    } else if (onInputChange && typeof onInputChange === "function") {
      debounceTimeoutRef.current = setTimeout(() => {
        onInputChange(val);
      }, 500);
    }
  };

  // Handle scroll for infinite loading
  const handleMenuScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isNearBottom = scrollHeight - scrollTop <= clientHeight + 10;
      const hasSearchTerm = searchTerm !== "";
      const scrollHandler = hasSearchTerm
        ? onSearchScrollToBottom
        : onMenuScrollToBottom;

      if (
        isNearBottom &&
        !isLoading &&
        !isSearching &&
        !isLoadingMore &&
        scrollHandler &&
        isAsyncMode
      ) {
        setIsLoadingMore(true);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

        scrollTimeoutRef.current = setTimeout(() => {
          if (hasSearchTerm && onSearchScrollToBottom) {
            onSearchScrollToBottom(searchTerm);
          } else if (onMenuScrollToBottom) {
            onMenuScrollToBottom();
          }
          setTimeout(() => setIsLoadingMore(false), 500);
        }, 100);
      }
    },
    [
      isLoading, isSearching, isLoadingMore,
      onMenuScrollToBottom, onSearchScrollToBottom, isAsyncMode, searchTerm,
    ],
  );

  // Update displayed options when parent appends new data
  useEffect(() => {
    if (isAsyncMode && !isLoadingMore && options.length > 0) {
      setDisplayedOptions((prevOptions) => {
        const optionsMap = new Map();
        prevOptions.forEach((opt) => optionsMap.set(opt.value, opt));
        options.forEach((opt) => optionsMap.set(opt.value, opt));
        return Array.from(optionsMap.values());
      });
    }
  }, [options, isAsyncMode, isLoadingMore]);

  // ── Handle option selection ────────────────────────────────────────────────
  const handleSelectOption = (option) => {
    let newSelectedValues;

    if (isMulti) {
      const isAlreadySelected = selectedValues.some(
        (item) => item.value === option.value,
      );
      newSelectedValues = isAlreadySelected
        ? selectedValues.filter((item) => item.value !== option.value)
        : [...selectedValues, option];
    } else {
      newSelectedValues = [option];
      setIsOpen(false);
      setSearchTerm("");
    }

    setSelectedValues(newSelectedValues);

    if (onChange) {
      // ── KEY FIX: for single-select, call onChange with the plain object
      // (or null) — not an array — so the parent's setFieldValue gets the
      // same shape that react-select would emit and Yup validation passes.
      if (isMulti) {
        onChange(newSelectedValues);
      } else {
        onChange(newSelectedValues.length > 0 ? newSelectedValues[0] : null);
      }
    }
  };

  // ── Clear single-select value ─────────────────────────────────────────────
  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled) return;
    setSelectedValues([]);
    if (onChange) onChange(isMulti ? [] : null);
  };

  // Remove one tag from multi-select
  const handleRemoveOption = (optionValue, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (isDisabled) return;
    const newSelectedValues = selectedValues.filter(
      (item) => item.value !== optionValue,
    );
    setSelectedValues(newSelectedValues);
    if (onChange) onChange(isMulti ? newSelectedValues : null);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < displayedOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && displayedOptions[highlightedIndex]) {
          handleSelectOption(displayedOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Handle refresh
  const handleRefresh = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setSearchTerm("");
    setCurrentSearchTerm("");
    setIsLocalSearching(false);
    setIsLoadingMore(false);
    setDisplayedOptions(defaultOptions);
    if (onRefresh) onRefresh();
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (isDisabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      if (onMenuOpen && typeof onMenuOpen === "function") onMenuOpen();
      if (searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  };

  // Render option label
  const renderOptionLabel = (option) => {
    if (formatOptionLabel) return formatOptionLabel(option);
    return option.label;
  };

  // Render selected item
  const renderSelectedItem = (item) => {
    if (!item || !item.label) return null;

    if (!isMulti) {
      // Single-select: plain text
      return (
        <span
          key={item.value}
          style={{
            display: "inline-block",
            padding: "6px 0",
            fontSize: "0.875rem",
            color: "var(--ink, #1C1712)",
            lineHeight: "1.4",
            flex: 1,
          }}
        >
          {renderOptionLabel(item)}
        </span>
      );
    }

    // Multi-select: tag with X
    return (
      <div
        key={item.value}
        style={{
          display: "inline-flex",
          alignItems: "stretch",
          backgroundColor: "var(--saffron-soft, #FDEBDD)",
          border: "1px solid #f6d9bf",
          fontSize: "0.875rem",
          marginRight: "8px",
          borderRadius: "7px",
          marginBottom: "6px",
          marginTop: "6px",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            padding: "8px 12px",
            backgroundColor: "transparent",
            color: "var(--saffron-deep, #D4530B)",
            fontWeight: 600,
            lineHeight: "1.4",
            wordBreak: "break-word",
            whiteSpace: "normal",
            flex: 1,
          }}
        >
          {renderOptionLabel(item)}
        </div>
        <button
          onClick={(e) => handleRemoveOption(item.value, e)}
          style={{
            width: "28px",
            padding: "0",
            backgroundColor: "var(--saffron, #F26A1B)",
            border: "none",
            borderLeft: "1px solid #f6d9bf",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            color: "white",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--saffron-deep, #D4530B)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--saffron, #F26A1B)"; }}
        >
          <FiX size={14} />
        </button>
      </div>
    );
  };

  // Render dropdown content
  const renderDropdownContent = () => {
    const showLoading = isLoading || isSearching || isLoadingMore;

    if (showLoading && displayedOptions.length === 0) {
      return (
        <div
          className="p-3 text-center"
          style={{ padding: "14px", textAlign: "center", color: "var(--ink-3, #9A8E7E)", fontSize: "0.875rem" }}
        >
          {/* Bootstrap's spinner-border is not loaded any more; the picker
              kit's ring is. */}
          <span
            className="nas-spinner"
            role="status"
            style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8, borderTopColor: "var(--saffron, #F26A1B)" }}
          >
            <span className="nas-sr">Loading...</span>
          </span>
          <span>{loadingMessage}</span>
        </div>
      );
    }

    if (!showLoading && displayedOptions.length === 0) {
      return (
        <div
          className="p-3 text-center"
          style={{ padding: "14px", textAlign: "center", color: "var(--ink-3, #9A8E7E)", fontSize: "0.875rem" }}
        >
          {searchTerm ? noSearchResultsMessage : noOptionsMessage}
        </div>
      );
    }

    if (displayedOptions.length > 0) {
      return (
        <>
          {displayedOptions.map((option, index) => {
            const isSelected = selectedValues.some(
              (item) => item.value === option.value,
            );
            const isHighlighted = index === highlightedIndex;

            return (
              <div
                key={option.value}
                className="p-2 d-flex align-items-center"
                onClick={() => handleSelectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 10px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--line, #EBE1D3)",
                  backgroundColor: isHighlighted ? "var(--saffron-soft, #FDEBDD)" : "var(--card, #fff)",
                  color: "var(--ink, #1C1712)",
                  minHeight: "36px",
                  fontSize: "0.875rem",
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  {isMulti && (
                    <div
                      className="me-2 d-flex align-items-center justify-content-center"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "8px",
                        width: "16px",
                        height: "16px",
                        border: isSelected ? "none" : "2px solid var(--line, #EBE1D3)",
                        borderRadius: "4px",
                        backgroundColor: isSelected ? "var(--saffron, #F26A1B)" : "var(--card, #fff)",
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && (
                        <div style={{ width: "8px", height: "8px", backgroundColor: "white", borderRadius: "1px" }} />
                      )}
                    </div>
                  )}
                  <div
                    style={{
                      fontWeight: isSelected ? "600" : "400",
                      color: isSelected ? "var(--saffron-deep, #D4530B)" : "var(--ink, #1C1712)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginLeft: isMulti ? "0" : "8px",
                      flex: 1,
                    }}
                  >
                    {renderOptionLabel(option)}
                  </div>
                </div>
              </div>
            );
          })}

          {isLoadingMore && displayedOptions.length > 0 && (
            <div className="p-2 text-center" style={{ padding: "8px", textAlign: "center" }}>
              <span className="nas-spinner" role="status" style={{ display: "inline-block", borderTopColor: "var(--saffron, #F26A1B)" }}>
                <span className="nas-sr">Loading more...</span>
              </span>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  const hasSingleValue = !isMulti && selectedValues.length > 0;

  /* Every layout class in this component -- position-relative,
     position-absolute, w-100, border, rounded, d-flex, align-items-center,
     p-2, p-3, me-2 -- is a Bootstrap utility, and this project ships no
     Bootstrap stylesheet. None of them resolve, so the menu had no
     position (top: 100% was ignored and it opened in the flow, pushing
     the rest of the page down), no width, no border, and its rows no
     padding. The geometry those class names describe is in the style
     attributes below, where the rest of this component already keeps it. */
  return (
    <div className="position-relative" ref={wrapperRef} style={{ position: "relative" }}>
      {/* Main container */}
      <div
        className={`form-control p-0 ${isDisabled ? "text-muted" : ""}`}
        style={{
          minHeight: "38px",
          height: "auto",
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: isDisabled ? "var(--paper-2, #FBF7F0)" : "var(--card, #fff)",
          borderColor: "var(--line, #EBE1D3)",
          borderRadius: "11px",
          position: "relative",
        }}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* Selected items container */}
        <div
          ref={selectedContainerRef}
          style={{
            maxHeight: `${maxSelectedHeight}px`,
            height: selectedContainerHeight,
            overflowY: shouldScroll ? "auto" : "visible",
            padding: isMulti ? "6px 30px 6px 12px" : "8px 56px 8px 12px",
            minHeight: "40px",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            alignItems: "center",
            backgroundColor: "transparent",
            transition: "height 0.2s ease",
          }}
        >
          {selectedValues.length > 0 ? (
            selectedValues.map((item) => renderSelectedItem(item))
          ) : (
            <span
              className="text-muted"
              style={{ fontSize: "0.875rem", padding: isMulti ? "8px 0" : "0" }}
            >
              {placeholder}
            </span>
          )}
        </div>

        {/* Right-side icons: clear (if applicable) + chevron */}
        <div
          className="d-flex align-items-center gap-1"
          style={{
            position: "absolute",
            right: "8px",
            top: "0",
            bottom: "0",
            backgroundColor: "transparent",
            paddingLeft: "8px",
          }}
        >
          {(isLoading || isSearching || isLoadingMore) && (
            <span className="nas-spinner" role="status" style={{ marginRight: 4, borderTopColor: "var(--saffron, #F26A1B)" }}>
              <span className="nas-sr">Loading...</span>
            </span>
          )}

          {/* Clear button for single-select with isClearable */}
          {isClearable && hasSingleValue && !isDisabled && (
            <button
              onClick={handleClear}
              style={{
                background: "none",
                border: "none",
                padding: "2px",
                cursor: "pointer",
                color: "var(--ink-3, #9A8E7E)",
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
              }}
              title="Clear"
            >
              <FiX size={14} />
            </button>
          )}

          <FiChevronDown
            style={{
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              color: "var(--ink-3, #9A8E7E)",
              pointerEvents: "none",
            }}
          />
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="position-absolute border rounded mt-1 w-100"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            marginTop: "4px",
            zIndex: 2100,
            maxHeight: `${maxMenuHeight}px`,
            overflow: "hidden",
            background: "var(--card, #fff)",
            border: "1px solid var(--line, #EBE1D3)",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(28,23,18,.14)",
          }}
        >
          {/* Search input */}
          {isSearchable && (
            <div
              className="p-2 border-bottom"
              style={{
                padding: "8px",
                borderBottom: "1px solid var(--line, #EBE1D3)",
              }}
            >
              <div className="input-group input-group-sm">
                <span
                  className="input-group-text"
                  style={{ background: "var(--card, #fff)", borderColor: "var(--line, #EBE1D3)", borderRight: "none" }}
                >
                  <FiSearch size={14} color="var(--ink-3, #9A8E7E)" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{ borderColor: "var(--line, #EBE1D3)", borderLeft: "none", fontSize: "0.875rem", color: "var(--ink, #1C1712)" }}
                />
                {onRefresh && (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleRefresh}
                    title="Refresh"
                    style={{ borderColor: "var(--line, #EBE1D3)", color: "var(--ink-3, #9A8E7E)" }}
                  >
                    <FiRefreshCcw size={14} />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Options list */}
          <div
            className="overflow-auto"
            style={{
              maxHeight: `${maxMenuHeight - (isSearchable ? 50 : 0)}px`,
              overflowY: "auto",
            }}
            onScroll={handleMenuScroll}
          >
            {renderDropdownContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default UniqueAsyncSelect;